import { ReactNode, useMemo, useState } from 'react';
import { OtsIconActionButton } from './OtsButton';
import {
  OtsDataTableRowHeader,
  OtsDataTableSortDirection,
  useOtsDataTableDensity,
} from './OtsDataTable';

export type OtsDataTableLinkProps = {
  onClick: () => void;
  children: ReactNode;
  /** Accessible name when the visible text alone is ambiguous. */
  ariaLabel?: string;
  className?: string;
};

/** A link-styled button for opening a row's record from inside a cell. */
export const OtsDataTableLink = ({
  onClick,
  children,
  ariaLabel,
  className,
}: OtsDataTableLinkProps) => (
  <button
    type="button"
    aria-label={ariaLabel}
    onClick={onClick}
    className={[
      'rounded-sm text-left font-semibold text-[var(--ots-primary)] underline-offset-2 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--ots-primary)]',
      className,
    ]
      .filter(Boolean)
      .join(' ')}
  >
    {children}
  </button>
);

export type OtsDataTablePrimaryCellProps = {
  /** The row's name; a link when `onOpen` is set. */
  title: ReactNode;
  /** Muted second line; long text is truncated with the full value as a tooltip. */
  subtitle?: ReactNode;
  onOpen?: () => void;
  /** Accessible name for the link, e.g. "Open Morning report". */
  openLabel?: string;
  /** Caps the cell width so long names wrap instead of widening the table. */
  maxWidthClassName?: string;
};

/** The row header: the record's name (linked to open it) with an optional detail line. */
export const OtsDataTablePrimaryCell = ({
  title,
  subtitle,
  onOpen,
  openLabel,
  maxWidthClassName = 'max-w-72',
}: OtsDataTablePrimaryCellProps) => (
  <OtsDataTableRowHeader className={maxWidthClassName}>
    {onOpen ? (
      <OtsDataTableLink onClick={onOpen} ariaLabel={openLabel}>
        {title}
      </OtsDataTableLink>
    ) : (
      <span className="font-semibold text-[var(--ots-secondary)]">{title}</span>
    )}
    {subtitle != null && subtitle !== '' && (
      <p
        className="truncate text-xs font-normal text-[var(--ots-text-muted)]"
        title={typeof subtitle === 'string' ? subtitle : undefined}
      >
        {subtitle}
      </p>
    )}
  </OtsDataTableRowHeader>
);

export type OtsDataTableRowAction = {
  /** Verb shown as the tooltip; the accessible name is "<label> <itemName>". */
  label: string;
  icon: ReactNode;
  onClick: () => void;
  /** Overrides the derived accessible name, e.g. "Recipients for Morning report". */
  ariaLabel?: string;
  disabled?: boolean;
  /** Hides the action, so callers can list every action and gate by permission. */
  hidden?: boolean;
};

export type OtsDataTableRowActionsProps = {
  actions: OtsDataTableRowAction[];
  /** The row's name, used to make each button's accessible name unique. */
  itemName: string;
};

/** Right-aligned icon buttons for a row (view, edit, copy, delete, …). */
export const OtsDataTableRowActions = ({
  actions,
  itemName,
}: OtsDataTableRowActionsProps) => {
  const density = useOtsDataTableDensity();
  const visible = actions.filter((action) => !action.hidden);
  if (visible.length === 0) return null;
  return (
    <div className="flex justify-end gap-1">
      {visible.map((action) => (
        <OtsIconActionButton
          key={action.label}
          variant="text"
          size={density === 'compact' ? 'mini' : 'compact'}
          icon={action.icon}
          label={action.ariaLabel ?? `${action.label} ${itemName}`}
          tooltip={action.label}
          disabled={action.disabled}
          onClick={action.onClick}
        />
      ))}
    </div>
  );
};

export type OtsDataTableEmptyProps = {
  children: ReactNode;
  /** Shows a loading message instead, with a polite live region. */
  loading?: boolean;
  loadingText?: ReactNode;
  compact?: boolean;
};

/** Dashed placeholder shown in place of a table with no rows. */
export const OtsDataTableEmpty = ({
  children,
  loading,
  loadingText = 'Loading…',
  compact,
}: OtsDataTableEmptyProps) => (
  <div
    role={loading ? 'status' : undefined}
    className={[
      'rounded-lg border border-dashed border-[var(--ots-outline-variant)] text-center text-[var(--ots-text-muted)]',
      compact ? 'p-4 text-xs' : 'p-6 text-sm',
    ].join(' ')}
  >
    {loading ? loadingText : children}
  </div>
);

export type OtsDataTableSort<K extends string> = {
  key: K;
  direction: OtsDataTableSortDirection;
};
export type OtsDataTableSortValue = string | number | null | undefined;

const isBlank = (value: OtsDataTableSortValue) =>
  value === undefined || value === null || value === '';

/** Numbers numerically, text case-insensitively with numeric awareness ("Pad 2" before "Pad 10"). */
export const compareOtsDataTableValues = (
  left: OtsDataTableSortValue,
  right: OtsDataTableSortValue
) =>
  typeof left === 'number' && typeof right === 'number'
    ? left - right
    : String(left).localeCompare(String(right), undefined, {
        numeric: true,
        sensitivity: 'base',
      });

/**
 * Pure client-side sort. Blank values always go last, whichever direction; `tieBreak` keeps
 * equal rows in a stable order (e.g. by id).
 */
export const sortOtsDataTableRows = <T, K extends string>(
  rows: T[],
  value: (row: T, key: K) => OtsDataTableSortValue,
  sort: OtsDataTableSort<K>,
  tieBreak?: (row: T) => OtsDataTableSortValue
) => {
  const factor = sort.direction === 'asc' ? 1 : -1;
  return [...rows].sort((left, right) => {
    const a = value(left, sort.key);
    const b = value(right, sort.key);
    if (isBlank(a) || isBlank(b)) {
      if (isBlank(a) && isBlank(b)) return 0;
      return isBlank(a) ? 1 : -1;
    }
    const result =
      compareOtsDataTableValues(a, b) ||
      (tieBreak
        ? compareOtsDataTableValues(tieBreak(left), tieBreak(right))
        : 0);
    return result * factor;
  });
};

/** Clicking the sorted column flips its direction; a new column starts ascending. */
export const nextOtsDataTableSort = <K extends string>(
  current: OtsDataTableSort<K>,
  key: K
): OtsDataTableSort<K> => ({
  key,
  direction:
    current.key === key && current.direction === 'asc' ? 'desc' : 'asc',
});

export type UseOtsDataTableSortOptions<T, K extends string> = {
  /** Controlled sort, e.g. kept in Redux so it survives navigation. */
  sort?: OtsDataTableSort<K>;
  onSortChange?: (sort: OtsDataTableSort<K>) => void;
  tieBreak?: (row: T) => OtsDataTableSortValue;
};

/**
 * Client-side sort for a table. `headerProps(key)` spreads onto OtsDataTableSortableHeaderCell.
 * Uncontrolled by default; pass `sort` + `onSortChange` to own the state.
 */
export const useOtsDataTableSort = <T, K extends string>(
  rows: T[],
  value: (row: T, key: K) => OtsDataTableSortValue,
  initial: OtsDataTableSort<K>,
  options: UseOtsDataTableSortOptions<T, K> = {}
) => {
  const [localSort, setLocalSort] = useState(initial);
  const sort = options.sort ?? localSort;
  const { onSortChange, tieBreak } = options;
  const setSort = (next: OtsDataTableSort<K>) => {
    if (options.sort === undefined) setLocalSort(next);
    onSortChange?.(next);
  };
  const sorted = useMemo(
    () => sortOtsDataTableRows(rows, value, sort, tieBreak),
    [rows, value, sort, tieBreak]
  );
  const headerProps = (key: K) => ({
    direction: sort.key === key ? sort.direction : undefined,
    onSort: () => setSort(nextOtsDataTableSort(sort, key)),
  });
  return { sorted, sort, setSort, headerProps };
};
